import { Component, OnInit, OnDestroy } from '@angular/core';
import { coreAnimations } from '../_shared/animations/core.anitmation';
import { AuthenticationService } from 'app/_shared/services';
import { Subscription, combineLatest, of } from 'rxjs';
import * as Models from 'app/_shared/models/models-index';
import moment from 'moment';
import { SummaryMetricsByDay } from './shared/summaryMetricsByDay.model';
import { FilterService, FilterMonth, FilterRetailer, FilterProgram } from './shared/filter.service';
import { ReportsService } from './shared/reports.service';
import { map, switchMap, startWith } from 'rxjs/operators';
import { ChartOptions } from 'chart.js';
import { RetailersService, ProgramsService } from 'app/_shared/services/api/api-index';
import { DecimalPipe, PercentPipe } from '@angular/common';
import * as Utilities from 'app/_shared/utilities/utilities-index';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CardChartModel } from './card-chart/cardChart.model';
import { BillboardChartModel } from './billboard-chart/billboardChart.model';
import { HoursMinutesPipe } from 'app/_shared/pipes/pipes-index';
import { CallBreakdownConfig, CallBreakdownConfigItem, DEFAULT_CALL_BREAKDOWN_CONFIG } from './shared/callBreakdownConfig.model';
import { Enums, IEnums } from '../app.enums';
import { IFacetedFilters } from 'app/filterPanel/types';
import { DateAdapter } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { DateRangeDialogComponent } from '../_shared/components/date-range-dialog/date-range-dialog.component';
import * as Entities from 'app/_shared/entities/index';
import { Router, ActivatedRoute } from '@angular/router';


@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    animations: coreAnimations,
    providers: [HoursMinutesPipe, DecimalPipe, PercentPipe],
    standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
  selectedMonthOrRange: FilterMonth;
  private _lastSelectedMonth: FilterMonth;
  customDateRange = 'custom';
  currentRetailer: FilterRetailer = { id: 0 };
  currentProgram: Entities.Program | null = null; // Will be set when programs load
  programs: Entities.Program[] = [];
  enums: IEnums = Enums;
  chartBlue = 'rgba(0, 153, 255, .6)';
  chartDataSetDefaults = { borderWidth: 1, borderColor: this.chartBlue, lineTension: 0.4 }
  initializing = true;
  filterWellInitialized = false;
  isConnectedRoute = false;
  private currentProgramConfig: CallBreakdownConfigItem[] | null = null;

  retailerAutocompleteControl = new UntypedFormControl();
  retailersFiltered$ = combineLatest([
    this.retailerAutocompleteControl.valueChanges.pipe(
      startWith('')
    ),
    this.retailersService.fetchAll().pipe(
      map(retailers => {
        const mapped = retailers.map(r => {
          return { id: r.id, name: r.name, displayName: `${r.name} (${r.code})` };
        });

        mapped.unshift({ id: 0, name: '', displayName: '' }); // shell for 'All Retailers'
        return mapped;
      })
    )
  ]).pipe(
    switchMap(([filter, retailers]) => {
      const normalize = (s: string): string => s?.trim().toLowerCase();

      const filterValue = typeof filter === 'string' ? filter : '';
      const normalizedFilter = normalize(filterValue);

      if (!normalizedFilter) {
        return of(retailers);
      }

      const filtered = retailers.filter(r => normalize(r.displayName).includes(normalizedFilter));
      return of(filtered);
    })
  );

  billboardAnimation = { value: '*', params: { scale: '.9', delay: '300ms' } };
  filterState: Models.FilterState;
  dashboardTitle = '';

  months: FilterMonth[] = [];
  maxReportDate = moment().toDate();
  reportDateRange = new UntypedFormGroup({
    start: new UntypedFormControl(moment().startOf('month')),
    end: new UntypedFormControl(moment())
  });

  summaryMetricsByDaySub: Subscription;
  filterServiceUpdatedSub: Subscription;

  private billboardChartSharedOptions = <BillboardChartModel>{
    type: 'line',
    labels: [],
    datasets: [],
    // TODO: chartjs
    options: <ChartOptions>{
      responsive: true,
      animation: false,
      legend: {
        display: false
      },
      tooltips: {
        display: false,
        enabled: true
      },
      gridLines: {
        display: false
      },
      fill: {
        target: 'origin',
        above: this.chartBlue,
      },
      elements: {
        point: {
          radius: 0
        }
      },
      scales: {
        xAxes: {
          gridLines: {
            color: 'rgba(0, 0, 0, 0)',
            zeroLineColor: 'rgba(0, 0, 0, 0)'
          },
          display: false
        },
        yAxes: {
          gridLines: {
            color: 'rgba(0, 0, 0, 0)',
            zeroLineColor: 'rgba(0, 0, 0, 0)'
          },
          display: false
        }
      }
    },
    iconName: '',
    title: '',
    delta: 0,
    graphRange: {},
    footer: {},
    highlights: [],
  };

  timeSavedBillboardChart = <BillboardChartModel>{
    ...this.billboardChartSharedOptions,
    iconName: 'history',
    title: 'Time Saved'
  };

  billableMinutesBillboardChart = <BillboardChartModel>{
    ...this.billboardChartSharedOptions,
    iconName: 'attach_money',
    title: 'Billable Minutes'
  };

  appointmentsBillboardChart = <BillboardChartModel>{
    ...this.billboardChartSharedOptions,
    iconName: 'calendar_today',
    title: 'Appointments'
  };

  followUpsBillboardChart = <BillboardChartModel>{
    ...this.billboardChartSharedOptions,
    iconName: 'assignment_late',
    title: 'Follow-ups'
  };

  breakdownChart = <CardChartModel>{
    responsive: true,
    title: 'Call Breakdown',
    type: 'pie',
    labels: [],
    iconName: 'pie_chart',
    data: {
      labels: ['Appointments', 'General Inquiries', 'Wrong Numbers', 'Miscellaneous', 'Hot Leads', 'Transfers to Retailer', 'Status Update Requests'],
      datasets: [{
        data: [0, 0, 0, 0, 0, 0, 0],
        hoverOffset: 4,
        backgroundColor: [
          '#42a5f5', // Appointments
          '#66bb6a', // General Inquiries
          '#ffa726', // Wrong Numbers
          '#ab47bc', // Miscellaneous
          '#26c6da', // Hot Leads
          '#ef5350', // Transfers to Retailer
          '#bdbdbd'  // Status Update Requests
        ]
      }]
    },
    options: <ChartOptions>{
      responsive: true,
      aspectRatio: 1,
      animation: false,
      plugins: {
        legend: {
          display: true,
          position: 'bottom'
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const data = context.dataset.data;
              const numericData = Array.isArray(data) ? data.filter(v => typeof v === 'number') : [];
              const total: number = (numericData.reduce((prev, curr) => (typeof prev === 'number' ? prev : 0) + (typeof curr === 'number' ? curr : 0), 0)) as number;
              const value: number = (typeof data[context.dataIndex] === 'number' ? data[context.dataIndex] : 0) as number;
              const label = context.label;
              const itemPercent = (total && value) ? Math.round((value / total) * 100) : 0;
              return `${label} - ${value} : ${itemPercent}%`;
            }
          }
        }
      }
    }
  };

  callVolumeChart = <CardChartModel>{
    title: 'Call Stats',
    type: 'line',
    labels: [],
    iconName: 'show_chart',
    data: {
      labels: [],
      datasets: [{
        label: 'Call Volume',
        data: [],
        backgroundColor: 'rgba(0, 153, 255, 0.2)',
        borderColor: 'rgba(0, 153, 255, 1)',
        fill: true,
        tension: 0.4,
        pointRadius: 3
      }]
    },
    options: <ChartOptions>{
      responsive: true,
      aspectRatio: 2,
      animation: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            // You can add custom tooltip formatting here if needed
          }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          title: { display: false }
        },
        y: {
          grid: { color: '#eee' },
          beginAtZero: true,
          title: { display: false }
        }
      },
      elements: {
        point: { radius: 3 }
      }
    }
  };

  constructor(
    private filterService: FilterService,
    private reportsService: ReportsService,
    private retailersService: RetailersService,
    private programsService: ProgramsService,
    private authService: AuthenticationService,
    private hoursMinutesPipe: HoursMinutesPipe,
    private decimalPipe: DecimalPipe,
    private percentPipe: PercentPipe,
    private dateAdapter: DateAdapter<Date>,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    // Check if we're on the connected route
    this.isConnectedRoute = this.router.url.includes('/connected');
    
    // For shift admins, initialize the filter well since they don't use the filter panel
    if (this.authService.isShiftAdmin()) {
      this.filterWellInitialized = true;
    }

    this.months = this.filterService.getMonths().sort((a, b) => +b.startDate - +a.startDate);
    const currentMonth = this.months[0];
    this.selectedMonthOrRange = currentMonth;
    this._lastSelectedMonth = currentMonth;
    this.reportDateRange.setValue({
      start: moment(currentMonth.startDate),
      end: moment(currentMonth.endDate)
    });

    this.filterService.resetFilterState();

    // Load programs and set default based on route
    this.loadPrograms();

    this.filterServiceUpdatedSub = this.filterService.updatedSubject.subscribe(filterState => {
      const filteredRetailer = filterState.filters.find(f => f.type === 'RetailerId');

      if (filteredRetailer) {
        let retailerId = parseInt(filteredRetailer.value, 10);
        this.retailersService.fetchRetailer(retailerId).subscribe(retailer => {

          if (retailer) {
            this.dashboardTitle = retailer.name;
          }
        });
        // if specific retailer, we aren't using the filter well, so bypass it
        this.filterWellInitialized = true;
      } else {
        this.dashboardTitle = '';
      }

      // Load program configuration for call breakdown chart
      const programFilter = filterState.filters.find(f => f.type === 'ProgramId');
      if (programFilter) {
        const programId = parseInt(programFilter.value, 10);
        this.loadProgramConfig(programId);
      } else {
        this.currentProgramConfig = null; // Use default
      }

      this.filterState = { ...filterState };
      this.loadDashboard();
    });

    this.dateAdapter.setLocale('en-US');
    // Set initial range to current month
    this.updateDashboardForDateRange();
  }

  ngOnDestroy(): void {
    if (this.summaryMetricsByDaySub) {
      this.summaryMetricsByDaySub.unsubscribe();
    }
    if (this.filterServiceUpdatedSub) {
      this.filterServiceUpdatedSub.unsubscribe();
    }
  }

  isRetailerUser(): boolean {
    return this.authService.isRetailerUser();
  }

  loadDashboard(): void {
    if (!this.filterWellInitialized)
      return;


    this.summaryMetricsByDaySub = this.reportsService.getSummaryMetricsByDay(this.filterState)
      .subscribe(summaryMetrics => {
        console.debug('filter state: ', this.filterState);
        this.getTimeSavedBillboardChart(summaryMetrics);
        this.getBillableMinutesBillboardChart(summaryMetrics);
        this.getAppointmentsBillboardChart(summaryMetrics);
        this.getFollowUpsBillboardChart(summaryMetrics);
        this.getCallBreakdownChart(summaryMetrics);
        this.getCallVolumeChart(summaryMetrics);

      });
  }

  changeRetailer(id: number): void {
    const retailer = <FilterRetailer>{ id: id };

    this.currentRetailer = retailer;
    this.filterService.updateRetailer(retailer);
  }

  loadPrograms(): void {
    this.programsService.getAll().subscribe(programs => {
      this.programs = programs;
      
      // Check for slug parameter in route
      const slug = this.route.snapshot.paramMap.get('slug');
      
      if (slug) {
        // Find program by slug
        const programBySlug = programs.find(p => p.slug === slug);
        if (programBySlug) {
          this.currentProgram = programBySlug;
          
          // If program is outbound, redirect to outbound dashboard
          if (programBySlug.isInbound === false) {
            // Navigate to outbound dashboard with the slug
            this.router.navigate(['/dashboard/outbound'], { 
              queryParams: { programId: programBySlug.programId } 
            });
            return;
          }
          
          const programFilter = <FilterProgram>{ id: programBySlug.programId };
          this.filterService.updateProgram(programFilter);
          return;
        }
      }
      
      if (this.isConnectedRoute) {
        // For connected route, find program with "starlink" in the name or program ID 5
        const starlinkProgram = programs.find(p => 
          p.name.toLowerCase().includes('starlink') || p.programId === 5
        ) || programs.find(p => p.programId === 5);
        
        if (starlinkProgram) {
          this.currentProgram = starlinkProgram;
          const programFilter = <FilterProgram>{ id: starlinkProgram.programId };
          this.filterService.updateProgram(programFilter);
        }
      } else {
        // For inbound route, set default to program 1 (first program or program with ID 1)
        const defaultProgram = programs.find(p => p.programId === 1) || programs[0];
        if (defaultProgram) {
          this.currentProgram = defaultProgram;
          const programFilter = <FilterProgram>{ id: defaultProgram.programId };
          this.filterService.updateProgram(programFilter);
        }
      }
    });
  }

  private loadProgramConfig(programId: number): void {
    this.programsService.getAll().subscribe(programs => {
      const program = programs.find(p => p.programId === programId);
      if (program?.callBreakdownConfig) {
        try {
          const config: CallBreakdownConfig = JSON.parse(program.callBreakdownConfig);
          this.currentProgramConfig = config.callBreakdown?.items || null;
        } catch (error) {
          console.warn('Error parsing call breakdown config:', error);
          this.currentProgramConfig = null; // Fallback to default
        }
      } else {
        this.currentProgramConfig = null; // Use default
      }
    });
  }


  getRetailerAutcompleteDisplayValue(value: any): string {
    return value?.displayName;
  }

  changeMonth(filterMonth: FilterMonth | 'custom'): void {
    if (filterMonth === 'custom') {
      const dialogRef = this.dialog.open(DateRangeDialogComponent, {
        data: {
          start: this.reportDateRange.value.start.toDate(),
          end: this.reportDateRange.value.end.toDate()
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.reportDateRange.setValue({
            start: moment(result.start),
            end: moment(result.end)
          });

          const customMonth: FilterMonth & { custom?: boolean } = {
            label: this.getReportDateRangeLabel(),
            startDate: result.start,
            endDate: result.end,
            custom: true
          };

          const existingCustomIndex = this.months.findIndex(m => (m as any).custom);
          if (existingCustomIndex > -1) {
            this.months[existingCustomIndex] = customMonth;
          } else {
            this.months.unshift(customMonth);
          }

          this.selectedMonthOrRange = customMonth;
          this._lastSelectedMonth = customMonth;
          this.updateDashboardForDateRange();
        } else {
          this.selectedMonthOrRange = this._lastSelectedMonth;
        }
      });
      return;
    }

    const selectedMonth = filterMonth as FilterMonth;
    this.selectedMonthOrRange = selectedMonth;
    this._lastSelectedMonth = selectedMonth;
    const today = moment();
    const filterEnd = moment(selectedMonth.endDate).subtract(1, 'day');
    const isCurrentMonth = filterEnd.month() === today.month() && filterEnd.year() === today.year();

    this.reportDateRange.setValue({
      start: moment(selectedMonth.startDate),
      end: isCurrentMonth ? today : filterEnd
    });

    this.updateDashboardForDateRange();
  }

  updateDashboardForDateRange(): void {
    if (!this.reportDateRange.value.end) {
      this.reportDateRange.setValue({
        ...this.reportDateRange.value,
        end: moment().endOf('month')
      });
    }

    const range = {
      start: moment(this.reportDateRange.value.start).toDate(),
      end: moment(this.reportDateRange.value.end).add(1, 'day').toDate()
    };

    this.filterService.updateDates(range.start, range.end, 'Custom Range');
  }

  onSelectedFilterChange($event: IFacetedFilters): void {
    this.filterService.updateRetailersAndOrgLookups($event.filters);
  }

  onFilterReset($event: IFacetedFilters): void {
    this.filterService.updateRetailersAndOrgLookups($event.filters);
  }

  onFilterWellInitialized($event: IFacetedFilters): void {
    this.filterWellInitialized = true;
    this.filterService.updateRetailersAndOrgLookups($event.filters);
  }

  private getRateChange(current: number, previous: number): number {
    return Utilities.safeDivide((current - previous), previous);
  }

  private getTimeSavedBillboardChart(summaryMetrics: SummaryMetricsByDay): void {
    this.timeSavedBillboardChart = {
      ...this.timeSavedBillboardChart,
      labels: summaryMetrics.records.map(r => r.date),
      datasets: [{ ...this.chartDataSetDefaults, data: summaryMetrics.records.map(r => r.minutesSaved) }],
      delta: this.getRateChange(summaryMetrics.minutesSavedTotal, summaryMetrics.previousMinutesSavedTotal),
      graphRange: {
        label: this.getReportDateRangeLabel(),
        value: this.hoursMinutesPipe.transform(summaryMetrics.minutesSavedTotal),
        hasTooltip: false
      },
      footer: {
        label: 'Previous:',
        value: this.hoursMinutesPipe.transform(summaryMetrics.previousMinutesSavedTotal),
        hasTooltip: false
      },
      highlights: [{
        label: 'Appointments:',
        value: this.decimalPipe.transform(summaryMetrics.appointmentsTotal),
        hasTooltip: false
      }, {
        label: 'Unable to Schedule:',
        value: this.decimalPipe.transform(summaryMetrics.unableToSchedulesTotal),
        hasTooltip: false
      }, {
        label: 'General Inquiry:',
        value: this.decimalPipe.transform(summaryMetrics.generalInquiriesTotal),
        hasTooltip: false
      }, {
        label: 'No Retailer Involvement:',
        value: this.percentPipe.transform(Utilities.safeDivide(summaryMetrics.withoutRetailerInvolvement, summaryMetrics.callVolumeTotal)),
        hasTooltip: false
      }]
    };
  }

  private getBillableMinutesBillboardChart(summaryMetrics: SummaryMetricsByDay): void {
    this.billableMinutesBillboardChart = {
      ...this.billableMinutesBillboardChart,
      labels: summaryMetrics.records.map(r => r.date),
      datasets: [{ ...this.chartDataSetDefaults, data: summaryMetrics.records.map(r => r.billableMinutes) }],
      delta: this.getRateChange(summaryMetrics.billableMinutesTotal, summaryMetrics.previousBillableMinutesTotal),
      graphRange: {
        label: this.getReportDateRangeLabel(),
        value: this.decimalPipe.transform(summaryMetrics.billableMinutesTotal),
        hasTooltip: false
      },
      footer: {
        label: 'Previous:',
        value: this.decimalPipe.transform(summaryMetrics.previousBillableMinutesTotal),
        hasTooltip: false
      },
      highlights: [{
        label: 'Calls Received:',
        value: this.decimalPipe.transform(summaryMetrics.callVolumeTotal),
        hasTooltip: false
      }, {
        label: 'Hours Saved:',
        value: summaryMetrics.minutesSavedTotal === 0 ? '0' : (summaryMetrics.minutesSavedTotal / 60).toFixed(2),
        hasTooltip: false
      }, {
        label: 'Avg. Call Time:',
        value: this.hoursMinutesPipe.transform(summaryMetrics.avgCallTime),
        hasTooltip: false
      }]
    };
  }

  private getAppointmentsBillboardChart(summaryMetrics: SummaryMetricsByDay): void {
    this.appointmentsBillboardChart = {
      ...this.appointmentsBillboardChart,
      labels: summaryMetrics.records.map(r => r.date),
      datasets: [{ ...this.chartDataSetDefaults, data: summaryMetrics.records.map(r => r.appointments) }],
      delta: this.getRateChange(summaryMetrics.appointmentsTotal, summaryMetrics.previousAppointmentsTotal),
      graphRange: {
        label: this.getReportDateRangeLabel(),
        value: this.decimalPipe.transform(summaryMetrics.appointmentsTotal),
        hasTooltip: false
      },
      footer: {
        label: 'Previous:',
        value: this.decimalPipe.transform(summaryMetrics.previousAppointmentsTotal),
        hasTooltip: false
      },
      highlights: [{
        label: 'Overall Appointment Rate:',
        value: this.percentPipe.transform(Utilities.safeDivide(summaryMetrics.appointmentsTotal, summaryMetrics.callVolumeTotal)),
        hasTooltip: false
      }, {
        label: '',
        value: '',
        hasTooltip: false
      }, {
        label: 'Appointment Minutes:',
        value: this.decimalPipe.transform(summaryMetrics.appointmentMinutesTotal),
        hasTooltip: false
      }]
    };
  }

  private getFollowUpsBillboardChart(summaryMetrics: SummaryMetricsByDay): void {
    this.followUpsBillboardChart = {
      ...this.followUpsBillboardChart,
      labels: summaryMetrics.records.map(r => r.date),
      datasets: [{ ...this.chartDataSetDefaults, data: summaryMetrics.records.map(r => r.followups) }],
      delta: this.getRateChange(summaryMetrics.followupsTotal, summaryMetrics.previousFollowupsTotal),
      graphRange: {
        label: this.getReportDateRangeLabel(),
        value: this.decimalPipe.transform(summaryMetrics.followupsTotal),
        hasTooltip: false
      },
      footer: {
        label: 'Previous:',
        value: this.decimalPipe.transform(summaryMetrics.previousFollowupsTotal),
        hasTooltip: false
      },
      highlights: [{
        label: 'Overall Follow-up Rate:',
        value: this.percentPipe.transform(Utilities.safeDivide(summaryMetrics.followupsTotal, summaryMetrics.callVolumeTotal)),
        hasTooltip: false
      }, {
        label: 'Hot Leads:',
        value: this.decimalPipe.transform(summaryMetrics.hotLeadsTotal),
        hasTooltip: false
      }, {
        label: 'Appt w/ Follow-ups:',
        value: this.decimalPipe.transform(summaryMetrics.appointmentFollowupsTotal),
        hasTooltip: false
      }]
    };
  }

  /**
   * Darkens a hex color by a specified amount (0-1)
   * @param hex - Hex color string (e.g., '#42a5f5')
   * @param amount - Amount to darken (0 = no change, 1 = black). Default 0.2 (20% darker)
   * @returns Darkened hex color string
   */
  private darkenColor(hex: string, amount: number = 0.2): string {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse RGB values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Darken each component
    const darkenedR = Math.max(0, Math.floor(r * (1 - amount)));
    const darkenedG = Math.max(0, Math.floor(g * (1 - amount)));
    const darkenedB = Math.max(0, Math.floor(b * (1 - amount)));
    
    // Convert back to hex
    return `#${darkenedR.toString(16).padStart(2, '0')}${darkenedG.toString(16).padStart(2, '0')}${darkenedB.toString(16).padStart(2, '0')}`;
  }

  private getCallBreakdownChart(summaryMetrics: SummaryMetricsByDay): void {
    // Get configuration (program-specific or default)
    const configItems = this.currentProgramConfig || DEFAULT_CALL_BREAKDOWN_CONFIG;
    
    // Filter visible items and sort by displayOrder
    const visibleItems = configItems
      .filter(item => item.visible)
      .sort((a, b) => a.displayOrder - b.displayOrder);
    
    // Map configuration to chart data
    const labels = visibleItems.map(item => item.label);
    const data = visibleItems.map(item => {
      // Map field name to SummaryMetricsByDay property
      const value = summaryMetrics[item.fieldMapping as keyof SummaryMetricsByDay] as number;
      return value || 0;
    });
    const colors = visibleItems.map(item => item.color);
    // Create darker shades for hover state
    const hoverColors = colors.map(color => this.darkenColor(color, 0.2));
    
    this.breakdownChart = {
      ...this.breakdownChart,
      data: {
        labels: labels, // Use configured labels
        datasets: [{
          data: data, // Use configured data mapping
          hoverOffset: 4,
          backgroundColor: colors, // Use configured colors
          hoverBackgroundColor: hoverColors // Use darker shades on hover
        }]
      }
    };
    console.debug('breakdownChart', this.breakdownChart);
  }

  private getCallVolumeChart(summaryMetrics: SummaryMetricsByDay): void {
    this.callVolumeChart = {
      ...this.callVolumeChart,
      data: {
        labels: summaryMetrics.records.map(r => moment(r.date).format('DD')),
        datasets: [{
          pointBackgroundColor: this.chartBlue,
          ...this.chartDataSetDefaults,
          data: summaryMetrics.records.map(r => r.callVolume),
        }]
      }
    };
  }

  getReportDateRangeLabel(): string {
    const format = (date: any) => moment(date).format('MM/DD/YYYY');
    return `${format(this.reportDateRange.value.start)} - ${format(this.reportDateRange.value.end)}`;
  }
}
